import axios from '../utils/axiosInstance';
import { User, CreateUserDTO, UpdateUserDTO } from '../types/user.d';

/**
 * Service for handling user-related API calls.
 */
export const UserService = {
    /**
     * Creates a new user with the provided data.
     * @param {CreateUserDTO} userData - Data for the new user.
     * @returns {Promise<User>} - Newly created user object.
     * @throws Will throw an error if the creation fails.
     */
    async createUser(userData: CreateUserDTO): Promise<User> {
        try {
            const response = await axios.post<User>('/UserInfo', userData, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            });
            return response.data;
        } catch (error: any) {
            throw error.response?.data || new Error('Erreur lors de la création de l’utilisateur');
        }
    },

    /**
     * Updates an existing user with the provided data.
     * @param {UpdateUserDTO} userData - Updated data for the user, must contain the user ID.
     * @returns {Promise<User>} - Updated user object.
     * @throws Will throw an error if the update fails.
     */
    async updateUser(userData: UpdateUserDTO): Promise<User> {
        try {
            const response = await axios.put<User>(`/UserInfo/${userData.keycloakId}`, userData, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            });
            return response.data;
        } catch (error: any) {
            throw error.response?.data || new Error('Erreur lors de la mise à jour de l’utilisateur');
        }
    },

    /**
     * Deletes a user by their ID.
     * @param {string} userId - ID of the user to delete.
     * @returns {Promise<void>} - Resolves if the deletion is successful.
     * @throws Will throw an error if the deletion fails.
     */
    async deleteUser(userId: string): Promise<void> {
        try {
            await axios.delete(`/UserInfo/${userId}`, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            });
        } catch (error: any) {
            throw error.response?.data || new Error('Erreur lors de la suppression de l’utilisateur');
        }
    },

    /**
     * Retrieves a specific user by their ID.
     * @param {string} userId - ID of the user to retrieve.
     * @returns {Promise<User>} - User object.
     * @throws Will throw an error if the request fails or if the user ID is invalid.
     */
    async getUserById(userId: string): Promise<User> {
        try {
            const response = await axios.get<User>(`/UserInfo/${userId}`, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            });
            return response.data;
        } catch (error: any) {
            throw error.response?.data || new Error('Erreur lors de la récupération de l’utilisateur');
        }
    },

    /**
     * Récupère la liste des rôles disponibles depuis l'API.
     * @returns {Promise<{ id: string; name: string }[]>} - Liste des rôles.
     * @throws Will throw an error if the request fails.
     */
    async getRoles(): Promise<{ id: string; name: string }[]> {
        try {
            const response = await axios.get<{ id: string; name: string }[]>('/Auth/roles', {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            });
            return response.data;
        } catch (error: any) {
            throw error.response?.data || new Error("Erreur lors de la récupération des rôles");
        }
    },
};

export default UserService;
